import { Component, OnInit } from '@angular/core';
import { ConfigService } from '@app/finxone-web-frontend/app/lib/services/config-service/config-service.service';
import { AutoDestroy } from '@finxone-platform/shared/base-types';
import {
  BaseCssProperties,
  ProviderClass,
  baseCssConfigStyle,
  buttonNestedWidgetConfigStyles,
} from '@finxone-platform/shared/sys-config-types';

import { AccountService } from '@app/finxone-web-frontend/app/lib/services/account-service/account-service.service';
import { CountriesCurrenciesService } from '@app/finxone-web-frontend/app/lib/services/countries-currencies-service/countries-currencies.service';
import {
  buildHandleBarModel,
  filterIfHandlebarNotRendered,
} from '@app/finxone-web-frontend/app/lib/utils/template/model-builder.utils';
import { IconCacheService } from '@finxone-platform/shared/services';
import { Store } from '@ngxs/store';
import Handlebars from 'handlebars';
import { Subject, takeUntil } from 'rxjs';
import { BaseComponent } from '../base/base.component';
export interface AccountRequirements {
  fullName?: {
    requirement: string;
  };
  firstName?: {
    requirement: string;
  };
  lastName?: {
    requirement: string;
  };
  businessName?: {
    requirement: string;
  };
  accountNumber?: {
    regex?: RegExp;
    requirement: string;
  };
  bankName?: {
    regex?: RegExp;
    requirement: string;
  };
  iban?: {
    regex?: RegExp;
    requirement: string;
  };
}

@Component({
  standalone: false,
  selector: 'finxone-web-frontend-international-beneficiary-credentials-widget',
  templateUrl: './international-beneficiary-credentials-widget.component.html',
})
export class InternationalBeneficiaryCredentialsWidgetComponent extends BaseComponent implements OnInit {
  @AutoDestroy destroy$: Subject<void> = new Subject<void>();

  public addressRequired: string;
  public recipientName: string;
  public firstName: string;
  public lastName: string;
  public beneficiaryType: string;
  public continueButtonContent = 'Continue';
  public previousButtonContent: string;
  public nextButtonContent: string;
  public continueButtonStyle: baseCssConfigStyle = {};
  public previousButtonStyle: baseCssConfigStyle = {};
  public nextButtonStyle: baseCssConfigStyle = {};

  public buttonGlobalStyles = {
    continueButton: {
      globalStylingClass: '',
      isAllowedGlobalStyling: false,
    },
    previousButton: {
      globalStylingClass: '',
      isAllowedGlobalStyling: false,
    },
    nextButton: {
      globalStylingClass: '',
      isAllowedGlobalStyling: false,
    },
  };
  public previousButtonText = '';
  public nextButtonText = '';
  public continueButtonText = '';

  constructor(
    public dataIn: ProviderClass<any>,
    private configService: ConfigService,
    public store: Store,
    private countriesCurrenciesService: CountriesCurrenciesService,
    private iconCacheService: IconCacheService,
    private accountService: AccountService,
  ) {
    super();
    this.recipientName = 'name';
    this.firstName = 'firstName';
    this.lastName = 'lastName';
  }
  ngOnInit(): void {
    this.beneficiaryType = this.dataIn.attri.widgetProperties['beneficiaryType'];
    if (this.dataIn.widgetConfig?.nestedWidgets?.['continueButton']) {
      this.getButtonContent('continueButton', 'Continue', 'continueButtonContent');

      this.continueButtonStyle = this.configService.getSpecificBaseCssStyle(
        this.getNestedWidgetConfig(this.dataIn, 'continueButton'),
        buttonNestedWidgetConfigStyles,
      );
    } else {
      this.continueButtonStyle = {};
    }

    if (this.dataIn.widgetConfig?.nestedWidgets?.['previousButton']) {
      this.getButtonContent('previousButton', 'Previous', 'previousButtonContent');

      this.previousButtonStyle = this.configService.getSpecificBaseCssStyle(
        this.getNestedWidgetConfig(this.dataIn, 'previousButton'),
        buttonNestedWidgetConfigStyles,
      );
    } else {
      this.previousButtonStyle = {};
    }

    if (this.dataIn.widgetConfig?.nestedWidgets?.['nextButton']) {
      this.getButtonContent('nextButton', 'Next', 'nextButtonContent');
      this.nextButtonStyle = this.configService.getSpecificBaseCssStyle(
        this.getNestedWidgetConfig(this.dataIn, 'nextButton'),
        buttonNestedWidgetConfigStyles,
      );
    } else {
      this.nextButtonStyle = {};
    }

    this.contentStyle = this.configService.getSpecificBaseCssStyle(this.dataIn.attri, [
      BaseCssProperties.PADDING,
      BaseCssProperties.BACKGROUND_COLOR,
      BaseCssProperties.MARGIN,
      BaseCssProperties.BORDER,
      BaseCssProperties.BOX_SHADOW,
      BaseCssProperties.COLOR,
      BaseCssProperties.TEXT_FONT_STYLE,
      BaseCssProperties.FONT_SIZE,
      BaseCssProperties.TEXTALIGN,
    ]);
    this.getGlobalStyling();
  }

  private getButtonContent(buttonKey: string, defaultText: string, buttonContentKey: string): void {
    const buttonText =
      this.dataIn.widgetConfig?.nestedWidgets?.[buttonKey]?.widgetProperties?.textContent ?? defaultText;
    const template = Handlebars.compile(buttonText);
    buildHandleBarModel(
      this.store,
      buttonText,
      this.countriesCurrenciesService,
      this.iconCacheService,
      this.accountService,
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe((model) => {
        (this as any)[buttonContentKey] = filterIfHandlebarNotRendered(template(model));
      });
  }

  private getGlobalStyling() {
    if (
      this.dataIn.widgetConfig.nestedWidgets?.['continueButton'].widgetProperties?.['globalStylingClass'] &&
      this.dataIn.widgetConfig.nestedWidgets?.['continueButton'].widgetProperties?.['globalStyling']
    ) {
      this.buttonGlobalStyles.continueButton.globalStylingClass =
        this.dataIn.widgetConfig.nestedWidgets?.['continueButton'].widgetProperties?.['globalStylingClass'] ??
        '';
      this.buttonGlobalStyles.continueButton.isAllowedGlobalStyling =
        this.dataIn.widgetConfig.nestedWidgets?.['continueButton'].widgetProperties?.['globalStyling'] ??
        false;
    }

    if (
      this.dataIn.widgetConfig.nestedWidgets?.['previousButton'].widgetProperties?.['globalStylingClass'] &&
      this.dataIn.widgetConfig.nestedWidgets?.['previousButton'].widgetProperties?.['globalStyling']
    ) {
      this.buttonGlobalStyles.previousButton.globalStylingClass =
        this.dataIn.widgetConfig.nestedWidgets?.['previousButton'].widgetProperties?.['globalStylingClass'] ??
        '';
      this.buttonGlobalStyles.previousButton.isAllowedGlobalStyling =
        this.dataIn.widgetConfig.nestedWidgets?.['previousButton'].widgetProperties?.['globalStyling'] ??
        false;
    }

    if (
      this.dataIn.widgetConfig.nestedWidgets?.['nextButton'].widgetProperties?.['globalStylingClass'] &&
      this.dataIn.widgetConfig.nestedWidgets?.['nextButton'].widgetProperties?.['globalStyling']
    ) {
      this.buttonGlobalStyles.nextButton.globalStylingClass =
        this.dataIn.widgetConfig.nestedWidgets?.['nextButton'].widgetProperties?.['globalStylingClass'] ?? '';
      this.buttonGlobalStyles.nextButton.isAllowedGlobalStyling =
        this.dataIn.widgetConfig.nestedWidgets?.['nextButton'].widgetProperties?.['globalStyling'] ?? false;
    }
  }

  get widgetProperties() {
    return this.dataIn.attri.widgetProperties;
  }
}
